import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { TbChevronLeft, TbX } from "react-icons/tb";
import PortaledModal from "../../../../components/Modals/PortaledModal";
import * as translator from "../../../../utils/translator";
import * as http from "../../../../utils/http";
import { useShippingPageContext } from "../providers/ShippingPageProvider";
import { useCartContext } from "../../../../customHooks/useCart";
import "../styles/SetLocationMapModal.css";

export type AddressFromMap = {
  address: string;
  city: string;
  zip: string;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onBack: () => void;
  onSave: (addressData: AddressFromMap) => void;
  isMobile?: boolean;
};

const COUNTRY_DEFAULT_CENTERS: Record<string, { lat: number; lng: number }> = {
  DE: { lat: 52.520008, lng: 13.404954 },
  FR: { lat: 48.856614, lng: 2.352222 },
  CH: { lat: 46.947974, lng: 7.447447 },
};

const FALLBACK_CENTER = { lat: 52.520008, lng: 13.404954 };

export function reverseGeocode(lat: number, lng: number): Promise<AddressFromMap | null> {
  return http
    .getFromServer(
      `v2/shipping/reverse-geocode?lat=${lat}&lng=${lng}`,
      [http.MIDDLEWARES.CUSTOMER_ID, http.MIDDLEWARES.CART_ORDER],
    )
    .then((response: any) => response?.data?.address ?? null)
    .catch(() => null);
}

const SetLocationMapModal: React.FC<Props> = ({ isOpen, onClose, onBack, onSave, isMobile = false }) => {
  const { language, isGoogleAPILoaded } = useShippingPageContext();
  const { cartCurrency } = useCartContext();
  const defaultCenter = useMemo(() => {
    const countryID = cartCurrency.countryID?.toUpperCase();
    return COUNTRY_DEFAULT_CENTERS[countryID] || FALLBACK_CENTER;
  }, [cartCurrency.countryID]);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const idleListenerRef = useRef<google.maps.MapsEventListener | null>(null);

  const [resolvedAddress, setResolvedAddress] = useState<AddressFromMap | null>(null);
  const [displayText, setDisplayText] = useState("");
  const [status, setStatus] = useState<"idle" | "searching" | "found" | "error">("idle");

  const handleAddressResolved = useCallback(
    (address: AddressFromMap | null, newStatus: "searching" | "found" | "error", text: string) => {
      setStatus(newStatus);
      setResolvedAddress(address);
      setDisplayText(text);
    },
    []
  );
  const callbackRef = useRef(handleAddressResolved);
  callbackRef.current = handleAddressResolved;

  useEffect(() => {
    // Reset state whenever the modal opens/closes so stale results from a previous
    // session don't linger (the host PortaledModal keeps children mounted).
    setResolvedAddress(null);
    setDisplayText("");
    setStatus("idle");

    if (!isOpen || !isGoogleAPILoaded || !mapContainerRef.current) return;
    if (mapInstanceRef.current) return;

    const map = new google.maps.Map(mapContainerRef.current, {
      center: defaultCenter,
      zoom: 15,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      zoomControl: true,
      keyboardShortcuts: false,
      gestureHandling: "greedy",
    });

    mapInstanceRef.current = map;

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const userLocation = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          map.setCenter(userLocation);
        },
        () => {},
        { timeout: 5000, enableHighAccuracy: true }
      );
    }

    idleListenerRef.current = map.addListener("idle", () => {
      const center = map.getCenter();
      if (!center) return;

      const lat = center.lat();
      const lng = center.lng();

      callbackRef.current(null, "searching", "");

      if (debounceRef.current) clearTimeout(debounceRef.current);

      debounceRef.current = setTimeout(() => {
        reverseGeocode(lat, lng).then((result) => {
          if (result) {
            callbackRef.current(result, "found", result.address);
          } else {
            callbackRef.current(null, "error", "");
          }
        });
      }, 600);
    });

    return () => {
      if (idleListenerRef.current) {
        google.maps.event.removeListener(idleListenerRef.current);
        idleListenerRef.current = null;
      }
      if (debounceRef.current) clearTimeout(debounceRef.current);
      mapInstanceRef.current = null;
    };
  }, [isOpen, isGoogleAPILoaded, defaultCenter]);

  const handleSave = () => {
    if (resolvedAddress) {
      onSave(resolvedAddress);
    }
  };

  const isSaveDisabled = !resolvedAddress || status !== "found";

  const searchingText = translator.translate(language, "shipping", "SetLocationMapModal/searching_address") || "Searching address";
  const bubbleText =
    status === "searching"
      ? searchingText
      : status === "found"
        ? displayText
        : "";

  const rootClasses = [
    "set-location-map-modal",
    isMobile ? "set-location-map-modal--mobile" : "set-location-map-modal--desktop",
  ].join(" ");

  const headerClasses = [
    "set-location-map-modal__header",
    isMobile ? "set-location-map-modal__header--mobile" : "",
  ].filter(Boolean).join(" ");

  return (
    <PortaledModal
      shouldShowModal={isOpen}
      hideModal={onClose}
      shouldBackgroundClickHideModal={true}
    >
      <div id="set-location-map-modal" className={rootClasses}>
        {/* Header */}
        <div className={headerClasses}>
          <button onClick={onBack} className="set-location-map-modal__icon-btn">
            <TbChevronLeft size={22} color="#333" />
          </button>
          <span className="set-location-map-modal__title">
            {translator.translate(language, "shipping", "SetLocationMapModal/title") || "Set location on a map"}
          </span>
          <button onClick={onClose} className="set-location-map-modal__icon-btn">
            <TbX size={20} color="#333" />
          </button>
        </div>

        {/* Subtitle */}
        <p className="set-location-map-modal__subtitle">
          {translator.translate(language, "shipping", "SetLocationMapModal/subtitle") ||
            "Move the map until you see the correct address"}
        </p>

        {/* Map container */}
        <div className="set-location-map-modal__map-wrapper">
          <div className="set-location-map-modal__map-frame">
            <div ref={mapContainerRef} className="set-location-map-modal__map" />

            {/* Center pin overlay */}
            <div className="set-location-map-modal__pin">
              <svg width="32" height="42" viewBox="0 0 32 42" fill="none">
                <path
                  d="M16 0C7.163 0 0 7.163 0 16c0 12 16 26 16 26s16-14 16-26C32 7.163 24.837 0 16 0z"
                  fill="#0A9281"
                />
                <circle cx="16" cy="16" r="6" fill="white" />
              </svg>
            </div>

            {/* Address bubble */}
            {bubbleText && (
              <div className="set-location-map-modal__bubble-container">
                <div className="set-location-map-modal__bubble">
                  <span className="set-location-map-modal__bubble-text">
                    {bubbleText}
                  </span>
                </div>
                <svg width="10" height="8" viewBox="0 0 10 8" fill="none" className="set-location-map-modal__bubble-arrow">
                  <polygon points="0,0 10,0 5,8" fill="#0a9281" />
                </svg>
              </div>
            )}
          </div>
        </div>

        {/* Save button */}
        <div className="set-location-map-modal__save-wrapper">
          <button
            onClick={handleSave}
            disabled={isSaveDisabled}
            className="set-location-map-modal__save"
          >
            {translator.translate(language, "shipping", "SetLocationMapModal/save") || "Save"}
          </button>
        </div>
      </div>
    </PortaledModal>
  );
};

export default SetLocationMapModal;
